<template>
  <mi-text-field
    ref="miTextField"
    :model-value="modelValue"
    :rules="searchFieldRules"
    class="search-field"
    data-cy="search-field"
    lazy-rules="ondemand"
    debounce="300"
    placeholder="Insert Options, Choices, Components and/or Component Variants"
    hint="To search by more than one parameter use ';' delimiter"
    outlined
    :clearable="!customClearIcon"
    @clear="$emit('update:model-value', '')"
    @focus="$emit('toggle:search-box', true)"
    @update:model-value="$emit('update:model-value', $event)"
  >
    <template #prepend>
      <!-- Search icon -->
      <mi-icon class="q-ml-xs" name="search" size="18px"></mi-icon>
    </template>
    <template #append>
      <mi-icon
        v-if="customClearIcon && modelValue !== ''"
        class="q-mr-sm"
        name="close-circle"
        size="16px"
        @mousedown.stop.prevent="clearInput()"
      ></mi-icon>
      <mi-icon
        v-if="!isSearchBoxExpanded"
        class="q-mr-xs"
        name="caret-down"
        size="18px"
        @click="$emit('toggle:search-box', false)"
      ></mi-icon>
      <mi-icon
        v-else
        class="q-mr-xs"
        name="caret-up"
        size="18px"
        @click="$emit('toggle:search-box', false)"
      ></mi-icon>
    </template>
  </mi-text-field>
</template>

<script>
  import { required, minLength } from '@/utils/validators'
  import MiIcon from '@/packages/@mi-library/MiIcon/MiIcon.vue'
  import MiTextField from '@/packages/@mi-library/MiTextField/MiTextField.vue'

  export default {
    name: 'SearchBoxField',
    components: { MiTextField, MiIcon },
    props: {
      isSearchBoxExpanded: {
        type: Boolean,
        required: false,
        default: false
      },
      modelValue: {
        type: String,
        required: true
      },
      selectedTypeLabel: {
        type: String,
        required: false,
        default: ''
      },
      customClearIcon: {
        type: Boolean,
        required: false,
        default: false
      },
      clearInput: {
        type: Function,
        required: false,
        default: () => {}
      }
    },
    emits: ['update:model-value', 'remove:selected-type', 'toggle:search-box'],
    data: () => ({
      searchFieldRules: [
        required,
        minLength(3)
      ]
    }),
    watch: {
      modelValue(current = '') {
        if (!current.trim().length) {
          this.removeSearchQueryFromRouteParams()
        }
      }
    },
    methods: {
      blur() {
        this.$refs.miTextField?.blur?.()
      },
      focus() {
        this.$refs.miTextField?.focus?.()
      },
      removeSearchQueryFromRouteParams() {
        const query = { ...this.$route.query }

        delete query.searchString

        this.$router.replace({ query })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .search-field {
    ::v-deep(.q-field__control),
    ::v-deep(.q-field__after) {
      background-color: $mi-white;
    }

    &.q-field--outlined ::v-deep(.q-field__control) {
      padding-left: .75rem;
      padding-right: .75rem;

      &:before,
      &:after {
        transition: none;
      }
    }

    ::v-deep(.q-field__after) {
      padding-right: .5rem;
      border-width: 2px 2px 2px 0;
      border-style: solid;
      border-color: $mi-field-border-color;
    }

    ::v-deep(.q-field__bottom) {
      padding-top: 6px;
    }

    &:not(.q-field--error) ::v-deep(.q-field__bottom) {
      color: $mi-anthracite-600;
    }

    &.q-field--error ::v-deep(.q-field__after) {
      border-color: $mi-red-700;
    }

    &:not(.q-field--error):hover ::v-deep(.q-field__control):before,
    &:not(.q-field--error):hover ::v-deep(.q-field__control):after,
    &:not(.q-field--error):hover ::v-deep(.q-field__after) {
      border-color: $mi-anthracite-800;
    }
  }
</style>
